import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import useCategorizationProgress from './useCategorizationProgress';

export const phase3SuggestionValue = suggestion => {
  if (!suggestion || suggestion.ai_error) return null;
  const value = suggestion.ai_suggestion;
  if (['Positive', 'Negative', 'Neutral', 'Undisclosed', 'Time Out'].includes(value)) return value;
  return ['Timeout', 'System Time Expired', 'SYSTEM_TIME_EXPIRED'].includes(value) ? 'Time Out' : null;
};

export const phase3SuggestionFor = (data, wordIndex, associationIndex) => {
  const word = data?.word_associations?.[wordIndex];
  const matches = (data?.ai_suggestions || []).filter(s =>
    s.word_index === wordIndex && s.association_index === associationIndex &&
    s.association === word?.associations?.[associationIndex] &&
    (s.word === undefined || s.word === word?.word));
  return matches.length === 1 ? matches[0] : null;
};

// No optimistic classification: dropdowns and counts only receive server data.
export default function usePhase3Valence({ sessionId, API, data, editable, refresh, revision }) {
  const scope = useRef(null);
  if (scope.current?.id !== sessionId) scope.current = { id: sessionId, attempts: new Set(), queues: new Map(), active: true, autoQueue: Promise.resolve() };
  const current = scope.current;
  const [state, setState] = useState({ pending: 0, error: '' });
  const [retry, setRetry] = useState(0);
  const live = () => scope.current === current && current.active;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    current.active = true;
    setState({ pending: 0, error: '' });
    return () => { current.active = false; };
  }, [current]);

  const reset = () => { current.attempts.clear(); current.halted = false; setState(s => ({ ...s, error: '' })); setRetry(n => n + 1); };
  const rows = (data?.word_associations || []).flatMap((word, wi) =>
    (word.associations || []).flatMap((_, ai) => {
      if (word.classifications?.[ai] != null && word.classifications[ai] !== '') return [];
      const suggestion = phase3SuggestionFor(data, wi, ai);
      return [{ suggestion, value: phase3SuggestionValue(suggestion) }];
    }));
  const progress = useCategorizationProgress({ phase: 3, sessionId, API, data, editable, rows,
    pending: state.pending, saveError: state.error, reset, refresh: () => refreshRef.current() });
  current.halted = Boolean(progress.error);
  const save = async (wordIndex, associationIndex, value, automatic = false) => {
    const word = data?.word_associations?.[wordIndex];
    if (!editable || !word || !live()) return;
    const key = `${wordIndex}:${associationIndex}`;
    const payload = { word_index: wordIndex, association_index: associationIndex,
      word: word.word, association: word.associations[associationIndex],
      classification: value, only_if_empty: automatic };
    revision.current += 1;
    setState(s => ({ ...s, pending: s.pending + 1, error: automatic ? s.error : '' }));
    // Serialize clinician selections for this row, including rapid reselection.
    // An AI request may run concurrently; the server's empty-only CAS arbitrates it.
    const previous = automatic ? Promise.resolve() : (current.queues.get(key) || Promise.resolve());
    const operation = previous.catch(() => {}).then(async () => {
      if (!live()) return;
      try {
        await axios.patch(`${API}/sessions/${sessionId}/phase3/classify`, payload, { timeout: 30000, skipGlobalLoading: true });
        if (live()) {
          revision.current += 1;
          const refreshed = await refreshRef.current();
          if (refreshed === false) throw new Error('Saved state could not be verified');
        }
      } catch (error) {
        current.halted = true;
        if (live()) setState(s => ({ ...s, error: 'Valence could not be saved or confirmed. Retry / check status or select a valence manually.' }));
      } finally {
        if (live()) setState(s => ({ ...s, pending: s.pending - 1 }));
      }
    });
    if (!automatic) current.queues.set(key, operation);
    return operation;
  };

  useEffect(() => {
    if (!editable || current.halted || data?.session_id !== sessionId) return;
    (data.word_associations || []).forEach((word, wi) => {
      (word.associations || []).forEach((association, ai) => {
        if (word.classifications?.[ai] != null && word.classifications[ai] !== '') return;
        const suggestion = phase3SuggestionFor(data, wi, ai);
        const value = phase3SuggestionValue(suggestion);
        if (!value) return;
        const key = JSON.stringify([wi, ai, word.word, association, suggestion]);
        if (current.attempts.has(key) || current.queues.has(`${wi}:${ai}`)) return;
        current.attempts.add(key);
        current.autoQueue = current.autoQueue.then(() => { if (live() && !current.halted) return save(wi, ai, value, true); });
      });
    });
  }, [data, editable, sessionId, retry]); // Intentionally keyed by persisted data, not renders.

  useEffect(() => {
    if (!editable || data?.session_id !== sessionId) return undefined;
    const waiting = rows.length > 0;
    if (!waiting) return undefined;
    const timer = setInterval(() => { if (live() && state.pending === 0) refreshRef.current(); }, 5000);
    return () => clearInterval(timer);
  }, [data, editable, sessionId, state.pending]);

  return { ...state, save, progress, retry: reset };
}
